import React from 'react';
import { Phone, Wrench, CheckCircle, AlertTriangle, Award } from 'lucide-react';
export function EngineRepair() {
  return <div className="min-h-screen bg-gray-50" data-id="element-1205">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#B1050B] to-[#8A0408] text-white py-16" data-id="element-1206">
        <div className="container mx-auto px-4" data-id="element-1207">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-id="element-1208">
            Engine Service and Repair – Major or Minor
          </h1>
          <p className="text-xl text-gray-100" data-id="element-1209">Gas Engines: Overview</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12" data-id="element-1210">
        <div className="max-w-4xl mx-auto" data-id="element-1211">
          {/* Introduction */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-1212">
            <p className="text-lg text-gray-700 mb-6" data-id="element-1213">
              You've trusted your vehicle for a long time. It's had its ups and
              downs, but it's always gotten you there… until recently. The old
              gas engine is giving up the ghost and you're facing some tough
              decisions. Do I have the engine overhauled or rebuilt, buy a used
              engine, or do I just bite the bullet and get another vehicle?
            </p>

            <p className="text-lg text-gray-700 mb-6" data-id="element-1214">
              The problem with overhauling or rebuilding an engine is that you
              don't really know what you're getting, and you can usually count
              on a minimal warranty, if any. Buying a newer vehicle may come
              with a longer warranty, but it's painful to lose thousands of
              dollars the moment you drive it off the lot.
            </p>

            <p className="text-2xl font-bold text-[#B1050B] text-center mb-6" data-id="element-1215">
              Take heart — there is a better alternative.
            </p>
          </div>

          {/* JASPER Solution */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-1216">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-1217">
              <Award className="mr-3 text-[#B1050B]" size={32} data-id="element-1218" />
              JASPER Remanufactured Engines
            </h2>

            <p className="text-lg text-gray-700 mb-6" data-id="element-1219">
              Install a completely remanufactured JASPER gas engine in your
              vehicle and get the best of both worlds. JASPER remanufactured
              engines are built to meet or exceed OEM specifications and include
              a{' '}
              <strong data-id="element-1220">
                3 year / 100,000 mile nationwide transferable warranty
              </strong>
              . Every engine is fully remanufactured with all new wear parts,
              tight tolerances, and run-tested reliability.
            </p>

            <p className="text-lg text-gray-700 mb-6" data-id="element-1221">
              Most JASPER remanufactured engines are available immediately
              through their nationwide distribution network, allowing you to get
              back on the road faster than waiting for a rebuild.
            </p>

            <div className="bg-blue-50 rounded-lg p-6" data-id="element-1222">
              <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-1223">
                JASPER Engine Benefits:
              </h3>
              <ul className="space-y-3 text-gray-700" data-id="element-1224">
                <li className="flex items-start" data-id="element-1225">
                  <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={20} data-id="element-1226" />
                  <span data-id="element-1227">
                    <strong data-id="element-1228">3 year / 100,000 mile warranty</strong> - Nationwide
                    and transferable
                  </span>
                </li>
                <li className="flex items-start" data-id="element-1229">
                  <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={20} data-id="element-1230" />
                  <span data-id="element-1231">Built to meet or exceed OEM specifications</span>
                </li>
                <li className="flex items-start" data-id="element-1232">
                  <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={20} data-id="element-1233" />
                  <span data-id="element-1234">All new wear parts and tight tolerances</span>
                </li>
                <li className="flex items-start" data-id="element-1235">
                  <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={20} data-id="element-1236" />
                  <span data-id="element-1237">Run-tested reliability before shipping</span>
                </li>
                <li className="flex items-start" data-id="element-1238">
                  <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={20} data-id="element-1239" />
                  <span data-id="element-1240">
                    Available immediately through nationwide distribution
                  </span>
                </li>
              </ul>
            </div>

            <div className="mt-6 text-center" data-id="element-1241">
              <a href="http://www.jasperengines.com/about-reman.htm" target="_blank" rel="noopener noreferrer" className="inline-block bg-[#B1050B] hover:bg-[#8A0408] text-white px-6 py-3 rounded-lg font-semibold transition-colors" data-id="element-1242">
                Compare Remanufactured vs Rebuilt Engines →
              </a>
            </div>
          </div>

          {/* Rebuilt vs Remanufactured Comparison */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-1243">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-1244">
              Rebuilt Engines vs Remanufactured Engines
            </h2>

            <div className="grid md:grid-cols-2 gap-6 mb-8" data-id="element-1245">
              {/* Rebuilt Engines */}
              <div className="border-2 border-yellow-300 rounded-lg p-6 bg-yellow-50" data-id="element-1246">
                <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center" data-id="element-1247">
                  <AlertTriangle className="mr-2 text-yellow-600" size={24} data-id="element-1248" />
                  Rebuilt Engines
                </h3>
                <p className="text-gray-700 mb-4" data-id="element-1249">
                  Rebuilt engines, often called overhauled engines, are
                  dismantled, inspected, cleaned, and repaired using a
                  combination of reused, reconditioned, and new parts. While
                  rebuilding costs less than replacing a vehicle, the quality
                  and longevity can vary widely.
                </p>
                <p className="text-gray-700 mb-4" data-id="element-1250">
                  Rebuilt engines may include older parts with unknown wear
                  histories. While they may come with a warranty, they typically
                  do not match the consistency, precision machining, or
                  longevity of remanufactured engines.
                </p>
                <div className="bg-white rounded p-4 mt-4" data-id="element-1251">
                  <p className="text-sm font-semibold text-gray-900 mb-2" data-id="element-1252">
                    Typical Characteristics:
                  </p>
                  <ul className="space-y-1 text-sm text-gray-700" data-id="element-1253">
                    <li data-id="element-1254">• Mix of old and new parts</li>
                    <li data-id="element-1255">• Unknown wear histories</li>
                    <li data-id="element-1256">• Variable quality</li>
                    <li data-id="element-1257">• Limited warranty</li>
                    <li data-id="element-1258">• Lower upfront cost</li>
                  </ul>
                </div>
              </div>

              {/* Remanufactured Engines */}
              <div className="border-2 border-green-300 rounded-lg p-6 bg-green-50" data-id="element-1259">
                <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center" data-id="element-1260">
                  <CheckCircle className="mr-2 text-green-600" size={24} data-id="element-1261" />
                  Remanufactured Engines
                </h3>
                <p className="text-gray-700 mb-4" data-id="element-1262">
                  Remanufactured engines are rebuilt by the original
                  manufacturer using specialized machining, precision
                  tolerances, and extensive testing. Cylinders are re-bored,
                  pistons matched, crankshafts reground, heads resurfaced, and
                  all wear components replaced.
                </p>
                <p className="text-gray-700 mb-4" data-id="element-1263">
                  Because remanufactured engines meet current OEM
                  specifications, they often run smoother, more efficiently, and
                  last longer than rebuilt engines. For high-mileage vehicles —
                  especially those exceeding 175,000 miles — remanufacturing is
                  often the smartest long-term solution.
                </p>
                <div className="bg-white rounded p-4 mt-4" data-id="element-1264">
                  <p className="text-sm font-semibold text-gray-900 mb-2" data-id="element-1265">
                    Typical Characteristics:
                  </p>
                  <ul className="space-y-1 text-sm text-gray-700" data-id="element-1266">
                    <li data-id="element-1267">• All new wear components</li>
                    <li data-id="element-1268">• Precision machining</li>
                    <li data-id="element-1269">• OEM specifications</li>
                    <li data-id="element-1270">• Extensive testing</li>
                    <li data-id="element-1271">• Comprehensive warranty</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-6" data-id="element-1272">
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-1273">
                The Remanufacturing Process:
              </h3>
              <ul className="space-y-2 text-gray-700" data-id="element-1274">
                <li className="flex items-start" data-id="element-1275">
                  <CheckCircle className="text-blue-600 mr-2 flex-shrink-0 mt-1" size={16} data-id="element-1276" />
                  <span data-id="element-1277">
                    <strong data-id="element-1278">Cylinders re-bored</strong> to precise
                    specifications
                  </span>
                </li>
                <li className="flex items-start" data-id="element-1279">
                  <CheckCircle className="text-blue-600 mr-2 flex-shrink-0 mt-1" size={16} data-id="element-1280" />
                  <span data-id="element-1281">
                    <strong data-id="element-1282">Pistons matched</strong> for optimal performance
                  </span>
                </li>
                <li className="flex items-start" data-id="element-1283">
                  <CheckCircle className="text-blue-600 mr-2 flex-shrink-0 mt-1" size={16} data-id="element-1284" />
                  <span data-id="element-1285">
                    <strong data-id="element-1286">Crankshafts reground</strong> to factory tolerances
                  </span>
                </li>
                <li className="flex items-start" data-id="element-1287">
                  <CheckCircle className="text-blue-600 mr-2 flex-shrink-0 mt-1" size={16} data-id="element-1288" />
                  <span data-id="element-1289">
                    <strong data-id="element-1290">Heads resurfaced</strong> for perfect sealing
                  </span>
                </li>
                <li className="flex items-start" data-id="element-1291">
                  <CheckCircle className="text-blue-600 mr-2 flex-shrink-0 mt-1" size={16} data-id="element-1292" />
                  <span data-id="element-1293">
                    <strong data-id="element-1294">All wear components replaced</strong> with new parts
                  </span>
                </li>
              </ul>
            </div>
          </div>

          {/* Recommendation */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-1295">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1296">
              Our Recommendation
            </h2>
            <p className="text-lg text-gray-700 mb-4" data-id="element-1297">
              For high-mileage vehicles — especially those exceeding 175,000
              miles — a remanufactured engine is often the smartest long-term
              solution. You get:
            </p>
            <ul className="space-y-3 text-gray-700 mb-6" data-id="element-1298">
              <li className="flex items-start" data-id="element-1299">
                <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={20} data-id="element-1300" />
                <span data-id="element-1301">Better reliability than a rebuilt engine</span>
              </li>
              <li className="flex items-start" data-id="element-1302">
                <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={20} data-id="element-1303" />
                <span data-id="element-1304">Lower cost than buying a new vehicle</span>
              </li>
              <li className="flex items-start" data-id="element-1305">
                <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={20} data-id="element-1306" />
                <span data-id="element-1307">Comprehensive warranty protection</span>
              </li>
              <li className="flex items-start" data-id="element-1308">
                <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={20} data-id="element-1309" />
                <span data-id="element-1310">Faster turnaround than custom rebuilds</span>
              </li>
            </ul>
            <p className="text-sm text-gray-600 italic" data-id="element-1311">
              Source: wisegeek.com/what-are-rebuilt-engines.htm
            </p>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-lg shadow-xl p-8 text-center" data-id="element-1312">
            <h2 className="text-3xl font-bold mb-4" data-id="element-1313">
              Need Engine Service or Replacement?
            </h2>
            <p className="text-xl mb-6" data-id="element-1314">
              Contact us to discuss your engine repair or replacement options
            </p>
            <a href="tel:2484788382" className="inline-flex items-center bg-[#B1050B] hover:bg-[#8A0408] text-white px-8 py-4 rounded-lg font-bold text-lg transition-colors shadow-lg" data-id="element-1315">
              <Phone className="mr-3" size={24} data-id="element-1316" />
              Give Us A Call: (248) 478-8382
            </a>
            <p className="mt-4 text-gray-300" data-id="element-1317">
              24175 Industrial Park Dr., Farmington Hills, MI 48335
            </p>
          </div>
        </div>
      </div>
    </div>;
}